import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Alert,
  Stack,
  ListItemIcon
} from '@mui/material';
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
// TODO: Intégrer avec l'API des utilisateurs côté serveur
// import { usersDB } from '../../db/database';

const UserMenu = () => {
  const { currentUser, login, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      setLoginDialogOpen(false);
      setEmail('');
      setPassword('');
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleChangePasswordClick = () => {
    handleClose();
    setChangePasswordOpen(true);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    try {
      // Vérifier que les champs sont remplis
      if (!passwordFormData.currentPassword || !passwordFormData.newPassword || !passwordFormData.confirmPassword) {
        throw new Error('Veuillez remplir tous les champs');
      }

      // Vérifier que les mots de passe correspondent
      if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
        throw new Error('Les nouveaux mots de passe ne correspondent pas');
      }

      // TODO: Implémenter le changement de mot de passe via l'API serveur
      console.log('Changement de mot de passe temporairement désactivé');
      alert('Fonctionnalité temporairement désactivée - migration en cours vers l\'API serveur');

      // Réinitialiser le formulaire et afficher le succès
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordSuccess('Votre mot de passe a été modifié avec succès');

      // Fermer le dialogue après un court délai
      setTimeout(() => {
        setChangePasswordOpen(false);
        setPasswordSuccess('');
      }, 2000);

    } catch (err) {
      setPasswordError(err.message);
    }
  };

  const getInitials = (user) => {
    if (!user) return '?';
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  };

  const getAvatarColor = (user) => {
    if (!user) return 'grey';
    switch (user.role) {
      case 'SUPER_ADMIN':
        return '#d32f2f';
      case 'ADMIN':
        return '#1976d2';
      default:
        return '#388e3c';
    }
  };

  return (
    <>
      {currentUser ? (
        <>
          <IconButton onClick={handleClick}>
            <Avatar 
              sx={{ 
                bgcolor: getAvatarColor(currentUser),
                width: 32,
                height: 32,
                fontSize: '0.875rem'
              }}
            >
              {getInitials(currentUser)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {currentUser.firstName} {currentUser.lastName}
                <br />
                <Typography variant="caption" color="textSecondary">
                  {currentUser.email}
                </Typography>
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleChangePasswordClick}>
              Changer mon mot de passe
            </MenuItem>
            <MenuItem onClick={handleLogout}>Déconnexion</MenuItem>
          </Menu>
        </>
      ) : (
        <Button 
          color="inherit" 
          onClick={handleLoginClick}
          sx={{ color: 'text.primary' }}
        >
          Connexion
        </Button>
      )}

      <Dialog 
        open={loginDialogOpen} 
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleLoginSubmit}>
          <DialogTitle>Connexion</DialogTitle>
          <DialogContent>
            {error && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <TextField
              margin="dense"
              label="Mot de passe"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLoginDialogOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained">Se connecter</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Changer mon mot de passe</DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                name="currentPassword"
                label="Mot de passe actuel"
                type="password"
                value={passwordFormData.currentPassword}
                onChange={handlePasswordChange}
                fullWidth
                required
              />

              <TextField
                name="newPassword"
                label="Nouveau mot de passe"
                type="password"
                value={passwordFormData.newPassword}
                onChange={handlePasswordChange}
                fullWidth
                required
              />

              <TextField
                name="confirmPassword"
                label="Confirmer le nouveau mot de passe"
                type="password"
                value={passwordFormData.confirmPassword}
                onChange={handlePasswordChange}
                fullWidth
                required
              />

              {passwordError && (
                <Alert severity="error">
                  {passwordError}
                </Alert>
              )}

              {passwordSuccess && (
                <Alert severity="success">
                  {passwordSuccess}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Modifier
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default UserMenu; 